import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Logo } from "./Logo";

const NAV = [
  { label: "À propos", hash: "#about" },
  { label: "Services", hash: "#services" },
  { label: "Approche", hash: "#approche" },
  { label: "Contact", hash: "#contact" },
];

export const Header = ({ forceSolid = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useEffect(() => {
    if (forceSolid) return; // No need to listen to scroll when header is forced solid
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [forceSolid]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const handleNavClick = (e, hash) => {
    e.preventDefault();
    setMenuOpen(false);
    if (isHome) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else {
      // Navigate to home then HomePage's effect will scroll to the hash
      navigate(`/${hash}`);
    }
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    setMenuOpen(false);
    if (isHome) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  const isSolid = forceSolid || scrolled || menuOpen;
  const headerClass = isSolid
    ? "bg-[#0A0A0A] border-b border-white/5"
    : "bg-transparent border-b border-transparent";

  return (
    <header
      data-testid="site-header"
      className={`site-header fixed top-0 left-0 right-0 z-50 ${headerClass}`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo */}
          <Link
            to="/"
            onClick={handleLogoClick}
            data-testid="site-logo-link"
            className="flex items-center gap-3 group"
            aria-label="LJMG — Accueil"
          >
            <Logo
              variant="light"
              className="h-9 md:h-10 w-auto"
              data-testid="site-logo-image"
            />
          </Link>

          {/* Desktop nav */}
          <nav
            aria-label="Navigation principale"
            className="hidden md:flex items-center gap-10"
            data-testid="desktop-nav"
          >
            {NAV.map((item) => (
              <a
                key={item.hash}
                href={isHome ? item.hash : `/${item.hash}`}
                onClick={(e) => handleNavClick(e, item.hash)}
                data-testid={`nav-link-${item.hash.replace("#", "")}`}
                className="nav-link text-[0.9rem] tracking-wide text-white/90 hover:text-white font-light"
              >
                {item.label}
              </a>
            ))}
          </nav>

          {/* Mobile burger */}
          <button
            type="button"
            onClick={() => setMenuOpen((s) => !s)}
            className="md:hidden text-white p-2 -mr-2"
            aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={menuOpen}
            data-testid="mobile-menu-toggle"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`md:hidden fixed inset-0 top-20 bg-[#0A0A0A] transition-opacity duration-300 ${
          menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        data-testid="mobile-menu-drawer"
      >
        <nav className="flex flex-col items-center justify-center h-full gap-10 -mt-20">
          {NAV.map((item) => (
            <a
              key={item.hash}
              href={isHome ? item.hash : `/${item.hash}`}
              onClick={(e) => handleNavClick(e, item.hash)}
              data-testid={`mobile-nav-link-${item.hash.replace("#", "")}`}
              className="font-serif text-3xl text-white hover:text-[#E10600] transition-colors"
            >
              {item.label}
            </a>
          ))}
          <span className="accent-bar mt-4" aria-hidden="true" />
        </nav>
      </div>
    </header>
  );
};
